import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { 
  SiLinux,
  SiApache,
  SiMysql,
  SiPhp,
  SiNodedotjs,
  SiMongodb,
  SiExpress,
  SiAngular,
  SiPython,
  SiDjango,
  SiNginx
} from 'react-icons/si';
import { productsService, ordersService } from '../../services';
import { ProductCategory } from '../../constants/productCategory';
import { useAuth } from '../../contexts/AuthContext';
import { Product } from '../../types/product.type';

interface StackConfig {
  name: string;
  description: string;
  type: string;
  version: string;
  instanceType: string;
  storage: number;
  environment: 'production' | 'staging' | 'development';
  region: string;
  autoBackup: boolean;
}

const CreateStack: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, isLoading: authLoading } = useAuth();
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState<StackConfig>({
    name: '',
    description: '',
    type: '',
    version: '',
    instanceType: 't3.medium',
    storage: 20,
    environment: 'production',
    region: 'us-east-1',
    autoBackup: true,
  });

  // Estado para almacenar los productos obtenidos de la API
  const [products, setProducts] = useState<Product[]>([]);
  
  // Estado para controlar la creación de la orden
  const [isCreating, setIsCreating] = useState(false);

  // Ref para evitar llamadas duplicadas (React.StrictMode ejecuta efectos dos veces en desarrollo)
  const hasFetchedProducts = useRef(false);

  // Obtener productos de la categoría BLUEPRINT al cargar el componente
  // Esperar a que el token esté disponible antes de hacer la petición
  useEffect(() => {
    // No hacer la petición si aún se está cargando la autenticación o si ya se hizo
    if (authLoading || hasFetchedProducts.current) return;
    
    hasFetchedProducts.current = true;

    const fetchProducts = async () => {
      // Si no hay token, esperar hasta 3 segundos antes de hacer la petición
      if (!accessToken) {
        console.log('No token available, waiting up to 3 seconds...');
        const { waitForToken } = await import('../../utils/tokenHelper');
        await waitForToken(3000, 200);
      }

      try {
        const response = await productsService.getProductsByCategory(ProductCategory.BLUEPRINT);
        console.log('Blueprint Products:', response.products);
        const productsArray = Array.isArray(response?.products) ? response.products : [];
        setProducts(productsArray);
      } catch (error) {
        console.error('Error fetching Blueprint products:', error);
        setProducts([]);
        hasFetchedProducts.current = false; // Permitir reintento en caso de error
      }
    };
    fetchProducts();
  }, [accessToken, authLoading]);

  const stackTypes = [
    { 
      id: 'lamp', 
      name: 'LAMP', 
      icon: <div className="flex items-center gap-2 text-2xl">
        <SiLinux className="text-orange-600" />
        <SiApache className="text-red-600" />
        <SiMysql className="text-blue-600" />
        <SiPhp className="text-indigo-600" />
      </div>,
      description: 'Linux, Apache, MySQL, PHP - The classic web development stack'
    },
    { 
      id: 'nodejs', 
      name: 'Node.js', 
      icon: <SiNodedotjs className="text-3xl" style={{ color: '#339933' }} />,
      description: 'Node.js runtime environment for building scalable network applications'
    },
    { 
      id: 'mean', 
      name: 'MEAN', 
      icon: <div className="flex items-center gap-2 text-2xl">
        <SiMongodb className="text-green-600" />
        <SiExpress className="text-gray-800" />
        <SiAngular className="text-red-600" />
        <SiNodedotjs className="text-green-600" />
      </div>,
      description: 'MongoDB, Express, Angular, Node.js - Full-stack JavaScript solution'
    },
    { 
      id: 'django', 
      name: 'Django', 
      icon: <SiDjango className="text-3xl" style={{ color: '#092E20' }} />,
      description: 'Django Python web framework with PostgreSQL and Nginx'
    },
    { 
      id: 'nginx', 
      name: 'Nginx', 
      icon: <SiNginx className="text-3xl" style={{ color: '#009639' }} />,
      description: 'High-performance web server and reverse proxy server'
    },
  ];

  const stackTypeDescriptions: Record<string, string> = {
    lamp: 'LAMP stack combines Linux operating system, Apache web server, MySQL database, and PHP programming language. Perfect for WordPress, Drupal, and traditional PHP applications.',
    nodejs: 'Node.js stack provides a JavaScript runtime built on Chrome\'s V8 engine. Ideal for building fast, scalable network applications and real-time applications.',
    mean: 'MEAN stack is a full-stack JavaScript solution using MongoDB, Express.js, Angular, and Node.js. Great for building dynamic web applications with a single language.',
    django: 'Django stack includes the Django Python web framework with PostgreSQL database and Nginx web server. Perfect for rapid development of secure and maintainable websites.',
    nginx: 'Nginx stack provides a high-performance web server, reverse proxy, and load balancer. Excellent for serving static content and handling high traffic loads.',
  };

  const getVersions = () => {
    switch (config.type) {
      case 'lamp':
        return [
          { id: 'php8.2', name: 'PHP 8.2' },
          { id: 'php8.1', name: 'PHP 8.1' },
          { id: 'php8.0', name: 'PHP 8.0' },
          { id: 'php7.4', name: 'PHP 7.4' },
        ];
      case 'nodejs':
        return [
          { id: 'node20', name: 'Node.js 20 LTS' },
          { id: 'node18', name: 'Node.js 18 LTS' },
          { id: 'node16', name: 'Node.js 16' },
        ];
      case 'mean':
        return [
          { id: 'latest', name: 'Latest Versions' },
          { id: 'stable', name: 'Stable Versions' },
        ];
      case 'django':
        return [
          { id: 'django5', name: 'Django 5.0' },
          { id: 'django4', name: 'Django 4.2' },
          { id: 'django3', name: 'Django 3.2' },
        ];
      case 'nginx':
        return [
          { id: 'nginx1.25', name: 'Nginx 1.25' },
          { id: 'nginx1.24', name: 'Nginx 1.24' },
          { id: 'nginx1.22', name: 'Nginx 1.22' },
        ];
      default:
        return [];
    }
  };

  const instanceTypes = [
    { id: 't3.micro', name: 't3.micro - 1 vCPU, 1 GB RAM' },
    { id: 't3.small', name: 't3.small - 2 vCPU, 2 GB RAM' },
    { id: 't3.medium', name: 't3.medium - 2 vCPU, 4 GB RAM' },
    { id: 't3.large', name: 't3.large - 2 vCPU, 8 GB RAM' },
    { id: 't3.xlarge', name: 't3.xlarge - 4 vCPU, 16 GB RAM' },
  ];

  const regions = [
    { id: 'us-east-1', name: 'US East (N. Virginia)' },
    { id: 'us-west-2', name: 'US West (Oregon)' },
    { id: 'eu-west-1', name: 'EU (Ireland)' },
    { id: 'ap-southeast-1', name: 'Asia Pacific (Singapore)' },
  ];

  const handleNext = () => {
    if (step < 3 && isStepValid()) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleCreate = async () => {
    if (!config.type || !config.version || !config.instanceType) {
      console.error('Missing required configuration');
      return;
    }

    setIsCreating(true);
    try {
      // Buscar el producto que coincida con el stack type seleccionado
      const selectedProduct = products.find(p => 
        p.name.toLowerCase().includes(config.type.toLowerCase()) ||
        p.description?.toLowerCase().includes(config.type.toLowerCase())
      );

      // Si no se encuentra un producto exacto, usar el primero disponible
      const productId = selectedProduct?.id || products[0]?.id;
      
      if (!productId) {
        console.error('No product found for stack');
        setIsCreating(false);
        return;
      }

      // Calcular el precio total (puedes ajustar esta lógica según tu modelo de precios)
      const basePrice = selectedProduct?.price || 39.99;
      const storageCost = (config.storage - 20) * 0.1; // $0.10 por GB adicional después de 20GB
      const backupCost = config.autoBackup ? 5 : 0;
      const totalAmount = basePrice + storageCost + backupCost;

      // Crear la orden
      const orderData = {
        productId: productId,
        quantity: 1,
        metadata: {
          type: 'app-server',
          stackConfig: {
            name: config.name,
            description: config.description,
            type: config.type,
            version: config.version,
            instanceType: config.instanceType,
            storage: config.storage,
            environment: config.environment,
            region: config.region,
            autoBackup: config.autoBackup,
          },
        },
      };

      console.log('Creating order with data:', orderData);
      const createdOrder = await ordersService.create(orderData);
      console.log('Order created successfully:', createdOrder);

      // Redirigir a la pantalla de pago con el orderId en la URL
      navigate(`/payment-selection/${createdOrder.id}`);
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Error al crear la orden. Por favor, intenta de nuevo.');
      setIsCreating(false);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return config.name && config.type && config.description;
      case 2:
        return config.version && config.instanceType;
      case 3:
        return true;
      default:
        return false;
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Stack Name *
              </label>
              <input
                type="text"
                value={config.name}
                onChange={(e) => setConfig({ ...config, name: e.target.value })}
                placeholder="my-lamp-stack"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={config.description}
                onChange={(e) => setConfig({ ...config, description: e.target.value })}
                placeholder="Brief description of your stack..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Stack Type *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {stackTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setConfig({ ...config, type: type.id, version: '' })}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      config.type === type.id
                        ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="mb-2 text-gray-700 dark:text-gray-300 flex items-center justify-start">
                      {type.icon}
                    </div>
                    <div className="font-medium text-gray-900 dark:text-white">{type.name}</div>
                  </button>
                ))}
              </div>
              {config.type && stackTypeDescriptions[config.type] && (
                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-semibold text-gray-900 dark:text-white">{stackTypes.find(t => t.id === config.type)?.name}:</span>{' '}
                    {stackTypeDescriptions[config.type]}
                  </p>
                </div>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Version *
              </label>
              <select
                value={config.version}
                onChange={(e) => setConfig({ ...config, version: e.target.value })}
                disabled={!config.type}
                className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none ${
                  !config.type ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : ''
                }`}
              >
                <option value="">
                  {config.type ? 'Select a version' : 'Select a stack type first'}
                </option>
                {getVersions().map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
              {!config.type && (
                <p className="text-xs text-gray-500 mt-1">
                  Please select a stack type first
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Instance Type *
              </label>
              <select
                value={config.instanceType}
                onChange={(e) => setConfig({ ...config, instanceType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              >
                {instanceTypes.map((it) => (
                  <option key={it.id} value={it.id}>
                    {it.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Storage (GB) *
              </label>
              <input
                type="number"
                min="20"
                max="1000"
                step="10"
                value={config.storage}
                onChange={(e) => setConfig({ ...config, storage: parseInt(e.target.value) || 20 })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                Minimum 20 GB, maximum 1000 GB
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Environment
              </label>
              <select
                value={config.environment}
                onChange={(e) => setConfig({ ...config, environment: e.target.value as StackConfig['environment'] })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              >
                <option value="production">Production</option>
                <option value="staging">Staging</option>
                <option value="development">Development</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Region
              </label>
              <select
                value={config.region}
                onChange={(e) => setConfig({ ...config, region: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              >
                {regions.map((region) => (
                  <option key={region.id} value={region.id}>
                    {region.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="autoBackup"
                checked={config.autoBackup}
                onChange={(e) => setConfig({ ...config, autoBackup: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="autoBackup" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Enable automatic backups
              </label>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 space-y-4">
              <div>
                <span className="text-sm font-medium text-gray-500">Stack Name</span>
                <p className="text-base text-gray-900 dark:text-white">{config.name}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Description</span>
                <p className="text-base text-gray-900 dark:text-white">{config.description || 'No description'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Type</span>
                <p className="text-base text-gray-900 dark:text-white">
                  {stackTypes.find(t => t.id === config.type)?.name || config.type}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Version</span>
                <p className="text-base text-gray-900 dark:text-white">
                  {getVersions().find(v => v.id === config.version)?.name || config.version || 'Not selected'}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Instance Type</span>
                <p className="text-base text-gray-900 dark:text-white">
                  {instanceTypes.find(it => it.id === config.instanceType)?.name || config.instanceType}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Storage</span>
                <p className="text-base text-gray-900 dark:text-white">{config.storage} GB</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Environment</span>
                <p className="text-base text-gray-900 dark:text-white capitalize">{config.environment}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Region</span>
                <p className="text-base text-gray-900 dark:text-white">
                  {regions.find(r => r.id === config.region)?.name || config.region}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Auto Backup</span>
                <p className="text-base text-gray-900 dark:text-white">{config.autoBackup ? 'Enabled' : 'Disabled'}</p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6 py-6">
      <button
        onClick={() => navigate('/stacks')}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
      >
        <ArrowLeftOutlined />
        <span className="text-sm font-medium">Back to App Server</span>
      </button>

      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Create New App Server</h1>
      <p className="text-gray-600 dark:text-gray-400 mb-8">Deploy a pre-configured application server</p>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[
            { num: 1, label: 'Stack Selection' },
            { num: 2, label: 'Configuration' },
            { num: 3, label: 'Review' },
          ].map((s, idx) => (
            <div key={s.num} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                    step >= s.num ? 'bg-black dark:bg-white text-white dark:text-black' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                  }`}
                >
                  {step > s.num ? <CheckCircleOutlined /> : s.num}
                </div>
                <span
                  className={`text-xs mt-2 font-medium ${
                    step >= s.num ? 'text-gray-900 dark:text-white' : 'text-gray-500'
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {idx < 2 && (
                <div
                  className={`h-1 flex-1 mx-2 rounded transition-all ${
                    step > s.num ? 'bg-black dark:bg-white' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 mb-6">
        {renderStep()}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={step === 1}
          className={`px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium transition-colors ${
            step === 1
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          Back
        </button>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/stacks')}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          {step < 3 ? (
            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                isStepValid()
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={isCreating}
              className="px-6 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? 'Creando orden...' : 'Create Order'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateStack;

